import { instrumentRunQueryKeys } from '@/config/queryKeys';
import { PLACEHOLDER_VALUE } from '@/constants';
import { SAMPLE_COUNT_FALLBACK } from '@/pages/Instrument_Runs/components/RunEditForm/assaySelection';
import type { InstrumentRunDataRow } from '@/types';

export const emptyRow = {
  study_name: PLACEHOLDER_VALUE,
  org_name: PLACEHOLDER_VALUE,
  is_control: false,
  sample_type: '',
};

// 2-plex numbering; reachable only from the fallback coordinate path below.
export const generateWellPosition = (i: number) => Math.ceil((i - 1) / 2 + 1);

/** Where customer samples sit on the plate: `plexity` demux indices per well, first well A`firstCustomerWellIndex`. */
export type CustomerPlateLayout = { plexity: number; firstCustomerWellIndex: number };

type ControlCoordinate = { well_position: string; demux_index: number };

const wellIndex = (well: string): number => Number.parseInt(well.replace(/^\D+/, ''), 10);

// Customers are multiplexed as deeply as the controls and start one well past the *last* control
// well (so a non-contiguous control block leaves its gaps unused), derived from
// `/assays/run_configs` so the grid stays correct when an assay changes.
export function deriveCustomerPlateLayout(controls?: ControlCoordinate[] | null): CustomerPlateLayout | undefined {
  if (!controls || controls.length === 0) return undefined;
  const plexity = Math.max(...controls.map(c => c.demux_index));
  const lastControlWell = Math.max(...controls.map(c => wellIndex(c.well_position)));
  if (!Number.isInteger(plexity) || plexity < 1 || !Number.isInteger(lastControlWell)) return undefined;
  return { plexity, firstCustomerWellIndex: lastControlWell + 1 };
}

const customerCoords = (i: number, { plexity, firstCustomerWellIndex }: CustomerPlateLayout) => ({
  demux_index: (i % plexity) + 1,
  well_position: `A${firstCustomerWellIndex + Math.floor(i / plexity)}`,
});

// Applies before an assay is picked, or when its control layout is unusable. Starts customers at
// A1, which collides with every assay whose controls occupy the low wells.
const fallbackCoords = (i: number, n: number) => {
  if (n > 24) return { demux_index: (i % 3) + 1, well_position: `A${Math.floor(i / 3) + 1}` };
  if (n > 12) return { demux_index: (i % 2) + 1, well_position: `A${generateWellPosition(i)}` };
  return { demux_index: 1, well_position: `A${i + 1}` };
};

/** Empty *customer* rows only — control rows come from the assay's own layout, not from here. */
export function createEmptyRows(count?: number, layout?: CustomerPlateLayout) {
  const n = count ?? 12;
  return Array.from({ length: n }, (_, i) => ({
    ...emptyRow,
    ...(layout ? customerCoords(i, layout) : fallbackCoords(i, n)),
    name: `Sample ${i + 1}`,
  }));
}

/**
 * Helper function to convert a string to camelCase
 * @param str - The string to convert
 * @returns The camelCase version of the string
 * @example toCamelCase('Name') // 'name'
 * @example toCamelCase('wellPosition') // 'wellPosition'
 */
export const toCamelCase = (str: string): string => str.charAt(0).toLowerCase() + str.slice(1);

/**
 * Extract file from various input formats that might be returned by file upload components
 * @param fileData - The data from the file upload component (could be File, FileList, Blob, string, or array)
 * @returns The extracted File, Blob, string, or null if invalid
 */
export const extractFile = (fileData: unknown): File | Blob | string | null => {
  if (typeof fileData === 'string') {
    return fileData;
  }

  // Handle FileList (most common from file input elements)
  if (fileData && typeof fileData === 'object' && 'length' in fileData && '0' in fileData) {
    const firstItem = (fileData as unknown[])[0];
    if (firstItem instanceof File) {
      return firstItem;
    }
  }

  // Handle direct File or Blob
  if (fileData instanceof File || fileData instanceof Blob) {
    return fileData;
  }

  // Handle array of files
  if (Array.isArray(fileData) && fileData.length > 0) {
    const firstFile = fileData[0];
    if (typeof firstFile === 'string' || firstFile instanceof File || firstFile instanceof Blob) {
      return firstFile;
    }
  }

  return null;
};

export const REQUIRED_HEADERS = [
  'Instrument Run',
  'Sample Name',
  'Sample Well Position',
  'Sample Demux Index',
  'Study',
  'Organization',
  'Library Group',
] as const;

/** Snapping a CSV's row count to one the assay rejects (Tau V1 takes only 12/24) is a 422 at
 *  draft-create, so `allowedCounts` — the assay's `valid_sample_counts` — wins when available. */
export const findNearestSampleCount = (actualCount: number, allowedCounts?: readonly number[]): string => {
  const options = allowedCounts && allowedCounts.length > 0 ? allowedCounts : SAMPLE_COUNT_FALLBACK;
  const nearest = options.reduce((prev, curr) =>
    Math.abs(curr - actualCount) < Math.abs(prev - actualCount) ? curr : prev,
  );
  return String(nearest);
};

const escapeCell = (cell: string) => {
  if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
    return `"${cell.replaceAll('"', '""')}"`;
  }
  return cell;
};

export const createSampleTemplate = (
  noOfSamples: number,
  selectedOrg?: { id?: string; name?: string } | null,
  instrumentRunName?: string,
  studyName?: string,
  layout?: CustomerPlateLayout,
): { blob: Blob; filename: string } => {
  const headers = [
    'Instrument Run',
    'Sample Name',
    'Sample Well Position',
    'Sample Demux Index',
    'Study',
    'Organization',
    'Library Group',
  ];

  // Same coordinates the grid generates, so a downloaded-then-reuploaded template round-trips.
  const rows = Array.from({ length: noOfSamples }, (_, i) => {
    const { well_position, demux_index } = layout ? customerCoords(i, layout) : fallbackCoords(i, noOfSamples);
    return [
      instrumentRunName || '',
      `Sample ${i + 1}`,
      well_position,
      String(demux_index),
      studyName || '',
      selectedOrg?.name || '',
      '',
    ];
  });

  const csvContent = [headers, ...rows].map(row => row.map(escapeCell).join(',')).join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const orgSuffix = selectedOrg?.id ? `_${selectedOrg.id}` : '';
  const filename = `sample_template${orgSuffix}.csv`;

  return { blob, filename };
};

/** Returns null rather than an `/orgs//samples/upload` URL when no org is selected, so callers can fail fast. */
export const resolveSampleUploadEndpoint = (orgId: string | null | undefined): string | null =>
  orgId ? instrumentRunQueryKeys.endpoints.samplesUpload(orgId) : null;

/** A CSV-upload response row, as returned by `/samples/upload` (`CSVParseResponse.samples`). */
export type Sample = {
  name: string;
  study_name: string;
  org_id?: string;
  org_name?: string;
  well_position: string;
  demux_index: number;
  library_group?: string | null;
};

/** Converts a raw CSV-upload response row into a grid row. Reads `is_control`/`sample_type`
 *  defensively (typeof-checked, not just optional-chained) since they aren't on `Sample` itself
 *  but can ride along on the wire response. */
export function mapCsvSamplesToRows(samples: Sample[]): InstrumentRunDataRow[] {
  return samples.map((sample: Sample) => {
    const raw = sample as unknown as Record<string, unknown>;
    const is_control = typeof raw.is_control === 'boolean' ? raw.is_control : false;
    const sample_type = typeof raw.sample_type === 'string' ? (raw.sample_type as string) : '';

    return {
      name: sample.name,
      study_name: sample.study_name,
      org_name: sample.org_name || '',
      org_id: '',
      well_position: sample.well_position,
      demux_index: sample.demux_index,
      is_control,
      sample_type,
      library_group: sample.library_group ?? null,
    };
  });
}
